<template>
  <div>
    <b-card class="mb-1">
      <b-row class="align-items-end">
        <b-col
          cols="12"
          md="9"
        >
          <b-form-group
            :label="$t('partner-admin.mappings.select-customer')"
            class="mb-1 mb-md-0 w-100"
          >
            <v-select
              v-model="selectedCustomerId"
              :options="customerOptions"
              :reduce="option => option.id"
              label="label"
              :placeholder="$t('partner-admin.mappings.select-customer-placeholder')"
              :disabled="loading || actionLoading"
              @search="handleCustomerSearch"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="3"
        >
          <b-button
            variant="primary"
            block
            :disabled="loading || actionLoading || !selectedCustomerId"
            class="mb-1 mb-md-0 w-100"
            @click="createMapping"
          >
            {{ $t('partner-admin.mappings.add-action') }}
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <b-card
      no-body
      class="mb-0"
    >
      <div class="p-1">
        <b-row class="align-items-end">
          <b-col
            cols="12"
            md="2"
            class="d-flex align-items-end"
          >
            <b-form-group
              :label="$t('partner-admin.filters.limit')"
              class="mb-1 mb-md-0 w-100"
            >
              <b-form-select
                v-model="query.limit"
                :options="limitOptions"
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="3"
            class="ml-auto"
          >
            <b-form-group
              :label="$t('partner-admin.fields.status')"
              class="mb-1 mb-md-0 w-100"
            >
              <b-form-select
                v-model="activeFilter"
                :options="activeFilterOptions"
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="4"
          >
            <b-form-group
              :label="$t('partner-admin.filters.search')"
              class="mb-1 mb-md-0 w-100"
            >
              <b-form-input v-model="query.q" />
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <rc-overlay :show="loading || listLoading || actionLoading">
        <b-table
          :items="mappings"
          :fields="fields"
          responsive
          show-empty
          :empty-text="(loading || listLoading) ? $t('shared.loading') : $t('shared.no-data')"
        >
          <template #cell(customerName)="data">
            <b-link
              class="text-nowrap"
              :to="{ name: 'customer-edit', params: { id: data.item.customerId } }"
            >
              {{ data.item.customerName || '-' }}
            </b-link>
          </template>

          <template #cell(isActive)="data">
            <b-badge :variant="data.item.isActive ? 'success' : 'secondary'">
              {{ data.item.isActive ? $t('partner-admin.mappings.active') : $t('partner-admin.mappings.inactive') }}
            </b-badge>
          </template>

          <template #cell(actions)="data">
            <div class="d-flex gap-1">
              <b-button
                size="sm"
                variant="outline-warning"
                :disabled="loading || actionLoading"
                @click="toggleMappingStatus(data.item)"
              >
                {{ data.item.isActive ? $t('partner-admin.mappings.deactivate') : $t('partner-admin.mappings.activate') }}
              </b-button>
              <b-button
                size="sm"
                variant="outline-danger"
                :disabled="loading || actionLoading"
                @click="deleteMapping(data.item.id)"
              >
                {{ $t('shared.remove') }}
              </b-button>
            </div>
          </template>
        </b-table>
      </rc-overlay>

      <table-bottom
        :query="query"
        :total="total"
      />
    </b-card>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCol,
  BLink,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BRow,
  BTable,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import RcOverlay from '@/layouts/components/rc/RcOverlay.vue'
import TableBottom from '@/layouts/components/table/TableBottom.vue'
import { parseRequestError } from '@/helpers/helpers'

export default {
  name: 'ExternalPartnerMappingsSection',
  components: {
    BBadge,
    BButton,
    BCard,
    BCol,
    BLink,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BRow,
    BTable,
    vSelect,
    RcOverlay,
    TableBottom,
  },
  props: {
    partnerId: {
      type: Number,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedCustomerId: null,
      actionLoading: false,
      listLoading: false,
      activeFilter: null,
      query: {
        q: null,
        isActive: null,
        limit: 25,
        page: 1,
        sortField: 'id',
        sortDir: 'ASC',
      },
      limitOptions: [10, 25, 50, 100].map(limit => ({ value: limit, text: limit })),
    }
  },
  computed: {
    mappings() {
      return this.$store.getters['externalPartner/getCustomerMappings']
    },
    total() {
      return this.$store.getters['externalPartner/getCustomerMappingsTotal']
    },
    customersLookup() {
      return this.$store.getters['externalPartner/getCustomersLookup']
    },
    customerOptions() {
      return this.customersLookup.map(customer => ({
        id: customer.id,
        label: customer.city ? `${customer.name} (${customer.city})` : customer.name,
      }))
    },
    activeFilterOptions() {
      return [
        {
          value: null,
          text: this.$t('partner-admin.filters.any-status'),
        },
        {
          value: 'active',
          text: this.$t('partner-admin.mappings.active'),
        },
        {
          value: 'inactive',
          text: this.$t('partner-admin.mappings.inactive'),
        },
      ]
    },
    fields() {
      return [
        { key: 'customerName', label: this.$t('partner-admin.mappings.fields.customer') },
        { key: 'customerCity', label: this.$t('partner-admin.mappings.fields.city') },
        { key: 'isActive', label: this.$t('partner-admin.mappings.fields.active') },
        { key: 'actions', label: this.$t('shared.actions') },
      ]
    },
  },
  watch: {
    partnerId: {
      immediate: true,
      handler() {
        this.initializeSection()
      },
    },
    activeFilter(newValue) {
      this.query.page = 1
      if (newValue === 'active') {
        this.query.isActive = true
        return
      }

      if (newValue === 'inactive') {
        this.query.isActive = false
        return
      }

      this.query.isActive = null
    },
    query: {
      deep: true,
      handler() {
        this.fetchMappingsList()
      },
    },
  },
  methods: {
    initializeSection() {
      this.query.page = 1
      this.fetchMappingsList()
      this.fetchCustomersLookup()
    },
    fetchMappingsList() {
      if (!this.partnerId) {
        return
      }

      this.listLoading = true
      const payload = {
        partnerId: this.partnerId,
        query: {
          limit: this.query.limit,
          page: this.query.page,
          sortField: this.query.sortField,
          sortDir: this.query.sortDir,
        },
      }

      if (this.query.q) {
        payload.query.q = this.query.q
      }

      if (this.query.isActive !== null) {
        payload.query.isActive = this.query.isActive
      }

      this.$store.dispatch('externalPartner/fetchPartnerCustomerMappings', payload)
        .catch(error => {
          parseRequestError(error)
          window.toast.notify.danger(this.$t('partner-admin.errors.fetch-mappings'))
        })
        .finally(() => {
          this.listLoading = false
        })
    },
    fetchCustomersLookup(q = null) {
      const query = {
        limit: 100,
        page: 1,
        sortField: 'name',
        sortDir: 'ASC',
      }

      if (q) {
        query.q = q
      }

      this.$store.dispatch('externalPartner/fetchCustomersLookup', query)
        .catch(error => {
          parseRequestError(error)
          window.toast.notify.danger(this.$t('partner-admin.errors.fetch-mappings'))
        })
    },
    handleCustomerSearch(searchText) {
      this.fetchCustomersLookup(searchText)
    },
    async createMapping() {
      this.actionLoading = true

      try {
        await this.$store.dispatch('externalPartner/createPartnerCustomerMapping', {
          partnerId: this.partnerId,
          data: {
            customerId: this.selectedCustomerId,
            isActive: true,
          },
        })
        this.selectedCustomerId = null
        this.fetchMappingsList()
      } catch (error) {
        parseRequestError(error)
        window.toast.notify.danger(this.$t('partner-admin.errors.create-mapping'))
      } finally {
        this.actionLoading = false
      }
    },
    async toggleMappingStatus(mapping) {
      this.actionLoading = true

      try {
        await this.$store.dispatch('externalPartner/updatePartnerCustomerMapping', {
          partnerId: this.partnerId,
          mappingId: mapping.id,
          data: {
            isActive: !mapping.isActive,
          },
        })
        this.fetchMappingsList()
      } catch (error) {
        parseRequestError(error)
        window.toast.notify.danger(this.$t('partner-admin.errors.update-mapping'))
      } finally {
        this.actionLoading = false
      }
    },
    async deleteMapping(mappingId) {
      this.actionLoading = true

      try {
        await this.$store.dispatch('externalPartner/deletePartnerCustomerMapping', {
          partnerId: this.partnerId,
          mappingId,
        })
        this.fetchMappingsList()
      } catch (error) {
        parseRequestError(error)
        window.toast.notify.danger(this.$t('partner-admin.errors.delete-mapping'))
      } finally {
        this.actionLoading = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.gap-1 {
  gap: 0.5rem;
}
</style>
